/** @jsxImportSource @emotion/react */
import { toast } from '@app/components/ui/Toast/toast'
import { Box, css } from '@mui/material'
import * as React from 'react'

type ExportColumn = {
  id: string
  header: string
}

type CsvExportRenderProps = {
  onClick: () => void
  disabled: boolean
}

type ExportDataTableProps = {
  columns: ExportColumn[]
  rows: Record<string, unknown>[]
  children: (props: CsvExportRenderProps) => React.ReactNode
  fileName?: string
}

const formatCsvValue = (value: unknown) => {
  if (value === null || value === undefined) {
    return ''
  }

  if (typeof value === 'string') {
    return value
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }

  if (value instanceof Date) {
    return value.toISOString()
  }

  return JSON.stringify(value)
}

const escapeCsvValue = (value: unknown) => {
  const formattedValue = formatCsvValue(value).replace(/\r?\n|\r/g, ' ')
  const escapedValue = formattedValue.replace(/"/g, '""')

  return `"${escapedValue}"`
}

export const ExportDataTable = ({
  columns,
  rows,
  fileName = 'risk-data-export.csv',
  children,
}: ExportDataTableProps) => {
  const handleExportCsv = React.useCallback(() => {
    if (columns.length === 0 || rows.length === 0) {
      return
    }

    const headers = columns.map((column) => escapeCsvValue(column.header)).join(',')
    const csvRows = rows.map((row) =>
      columns.map((column) => escapeCsvValue(row[column.id])).join(','),
    )

    const csvContent = [headers, ...csvRows].join('\n')
    const blob = new Blob(['\uFEFF', csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = url
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()

    toast({
      type: 'success',
      description: 'CSV downloaded successfully',
      position: 'top-center',
    })
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }, [columns, fileName, rows])

  return (
    <Box css={exportContainer}>
      {children({ onClick: handleExportCsv, disabled: rows.length === 0 || columns.length === 0 })}
    </Box>
  )
}

export const exportContainer = css`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
`
